/**
 * The session screen — the shell the five tabs live in.
 *
 * Tap 1: the winning side's Won button (PlayScreen).
 * Tap 2: the losing score (ScoreGrid).
 *
 * Both are rendered from data already in hand, so neither tap waits on the
 * network. A non-default target costs one extra tap on the Play screen before
 * the game (FR-ENTRY-5); replacing a player costs two, with no mode to enter
 * or leave (FR-ENTRY-4a, AC-27d).
 *
 * ── the tabs ─────────────────────────────────────────────────────────────────
 *
 *   Play    the entry path, and nothing else
 *   Games   every result, in order, editable
 *   Table   today's standings and pairs, or the club all-time behind a toggle
 *   End     pre-flight, commit, share
 *   You     account, roster, the way to the backup tracker
 *
 * Games has its own tab because the mockup has one and the reason holds: the
 * list is what somebody opens to answer "did that one get logged?", and it was
 * buried under the queue at the bottom of the entry screen.
 *
 * The club leaderboard sits behind a toggle on Table rather than taking a
 * sixth tab. Five is already the most a thumb can reach reliably, and "today"
 * and "all time" are the same question over different games.
 *
 * The score grid deliberately has no tab bar. Mid-result the only ways out are
 * a score or Cancel; a stray tap on Table would silently discard the game that
 * was half-entered.
 */

import { useEffect, useRef, useState } from "react";
import { api, type Me, type SessionView, type TournamentFixture } from "../api.js";
import { SyncIndicator } from "../sync/SyncIndicator.js";
import type { UseSync } from "../sync/useSync.js";
import type { SyncOp } from "../sync/queue.js";
import { ScoreGrid } from "./ScoreGrid.js";
import { AttendanceSheet } from "./AttendanceSheet.js";
import { ClubBoard } from "./ClubBoard.js";
import { CommitScreen } from "./CommitScreen.js";
import { GamesList } from "./GamesList.js";
import { PlayScreen } from "./PlayScreen.js";
import { YouScreen } from "./YouScreen.js";
import { Standings } from "./Standings.js";
import { TournamentPlay } from "./TournamentPlay.js";
import { TournamentTable } from "./TournamentTable.js";
import { formatSessionDate } from "./StartSession.js";

type Pending = {
  winnerSide: "A" | "B";
  sideA: string[];
  sideB: string[];
  /** FR-TOUR-4 — set when the result being entered settles a fixture. */
  fixture?: TournamentFixture;
} | null;
type Tab = "play" | "games" | "table" | "finish" | "settings";

export function SessionScreen({
  me,
  view,
  sync,
  onChanged,
  onLeave,
  onOpenSession,
  onShowGuide,
  onSignedOut,
  recordGame,
}: {
  me: Me;
  view: SessionView;
  sync: UseSync;
  onChanged: () => Promise<void>;
  onLeave: () => void;
  onOpenSession?: (sessionId: string) => void;
  onShowGuide?: () => void;
  onSignedOut: () => void;
  recordGame: (
    sideA: string[],
    sideB: string[],
    sideAScore: number,
    sideBScore: number,
    targetScore?: number,
    gameType?: "doubles" | "singles",
    courtNumber?: number,
    tournamentFixtureId?: string,
  ) => Promise<SyncOp>;
}): React.JSX.Element {
  const { session, queue, entry, games, tournament } = view;
  const [tab, setTab] = useState<Tab>("play");
  const [pending, setPending] = useState<Pending>(null);
  const [target, setTarget] = useState(entry.targetScore);
  const [swapSlot, setSwapSlot] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ text: string; gameId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRoster, setShowRoster] = useState(false);
  const [allTime, setAllTime] = useState(false);
  // Only set when the member switches to singles; doubles arrives with the
  // session view and BR-STAT-3 keeps the two apart.
  const [standingsShown, setStandingsShown] = useState<SessionView["standings"] | null>(null);
  const [override, setOverride] = useState<{ sideA: string[]; sideB: string[] } | null>(null);
  // FR-ENTRY-6 - doubles pre-selected; singles is one tap away and rare.
  const [gameType, setGameType] = useState<"doubles" | "singles">("doubles");
  // FR-ENTRY-16 - which court this result belongs to, on a multi-court session.
  const [court, setCourt] = useState<number | null>(null);

  const nameOf = (id: string): string =>
    queue.find((q) => q.playerId === id)?.displayName ??
    // A tournament's draw is the roster, and somebody substituted in mid-round
    // may not be in the queue this render. Falling through to "Unknown" on the
    // score screen would be the last thing a member sees before confirming.
    tournament?.teams
      .flatMap((t) => t.members)
      .find((m) => m.playerId === id)?.displayName ??
    "Unknown";

  // FR-ENTRY-9 — the confirmation stands for about four seconds with a one-tap
  // Undo. After it goes, the most recent game is still one tap away on the Last
  // game card, so nothing becomes unreachable when the toast clears.
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  function showToast(text: string, gameId: string): void {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ text, gameId });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }

  // Creates this device is still holding, newest first.
  const queuedGames = sync.pendingOps
    .filter((op) => op.opType === "create_game" && op.payload.sessionId === session.id)
    .reverse();

  const matchup = gameType === "singles" ? entry.singlesMatchup : entry.matchup;
  const multiCourt = entry.courtCount > 1;

  /**
   * ENTRY-19 — "no dead end". The server proposes nothing on a multi-court
   * session, by design (FR-ENTRY-13: players self-organise and the app
   * observes). But once a court is chosen, or singles is selected, the screen
   * still has to offer something to tap.
   *
   * So it falls back to the first players in the queue. That is a starting
   * point the member can change with two taps, not a proposal: the queue does
   * not advance and nothing is implied about whose turn it is.
   */
  function fromQueue(count: number): { sideA: string[]; sideB: string[] } | null {
    const available = queue.filter((q) => !q.isOutOfRotation).map((q) => q.playerId);
    if (available.length < count) return null;
    return count === 2
      ? { sideA: [available[0]], sideB: [available[1]] }
      : { sideA: available.slice(0, 2), sideB: available.slice(2, 4) };
  }

  const proposed = matchup.available
    ? { sideA: matchup.sideA, sideB: matchup.sideB }
    : fromQueue(gameType === "singles" ? 2 : 4);

  const sides = override ?? proposed;

  // On a multi-court session nothing is proposed until a court is chosen
  // (FR-ENTRY-13). Choosing one is the extra tap ENTRY-03 accounts for.
  const awaitingCourt = multiCourt && court === null;

  const onCourt = sides ? [...sides.sideA, ...sides.sideB] : [];
  const waiting = queue.filter((q) => !q.isOutOfRotation && !onCourt.includes(q.playerId));

  async function saveResult(loserScore: number, winnerScore: number): Promise<void> {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const [sideAScore, sideBScore] =
        pending.winnerSide === "A" ? [winnerScore, loserScore] : [loserScore, winnerScore];
      const op = await recordGame(
        pending.sideA,
        pending.sideB,
        sideAScore,
        sideBScore,
        // A tournament is played to its own target on every court, and the
        // Play screen offers no target toggle, so the session's is always the
        // right one. The cap never travels: the server reads it (BR-SCORE-1a).
        pending.fixture ? entry.targetScore : target,
        pending.fixture ? "doubles" : gameType,
        pending.fixture ? pending.fixture.courtNumber : (court ?? undefined),
        pending.fixture?.id,
      );
      const winners = (pending.winnerSide === "A" ? pending.sideA : pending.sideB).map(nameOf);
      showToast(`${winners.join(" & ")} ${winnerScore}-${loserScore}`, op.clientUuid);
      setPending(null);
      setOverride(null);
      setTarget(entry.targetScore);
      // Both are per-game choices, so they go back to the default rather than
      // quietly persisting into the next result.
      setGameType("doubles");
      setCourt(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save that game.");
    } finally {
      setBusy(false);
    }
  }

  // ── tap 2: the score. No tab bar here, on purpose. ────────────────────────
  if (pending) {
    return (
      <ScoreGrid
        targetScore={pending.fixture ? entry.targetScore : target}
        // BR-SCORE-0 / AC-28 — the grid follows the target chosen on Play. At 15 it runs 0-13
        // and tops out at 21-20; at 21 it runs 0-19 and tops out at 30-29.
        // BR-SCORE-1a — under a tournament cap it is shorter still: at 15 with
        // a dead stop at 17, the only deuce results offered are 16-14, 17-15
        // and 17-16, so anything past the cap is unenterable rather than
        // rejected (FR-ENTRY-8).
        options={
          tournament
            ? tournament.scoreOptions
            : (entry.scoreOptionsByTarget[String(target)] ?? entry.scoreOptions)
        }
        winnerNames={(pending.winnerSide === "A" ? pending.sideA : pending.sideB).map(nameOf)}
        loserNames={(pending.winnerSide === "A" ? pending.sideB : pending.sideA).map(nameOf)}
        busy={busy}
        error={error}
        onPick={saveResult}
        onCancel={() => setPending(null)}
      />
    );
  }

  if (showRoster) {
    return <AttendanceSheet view={view} onClose={() => setShowRoster(false)} onChanged={onChanged} />;
  }

  const nav = (
    <TabBar
      tab={tab}
      onTab={setTab}
      games={games.length + queuedGames.length}
      unsent={sync.status.counts.pending + sync.status.counts.inflight}
    />
  );

  if (tab === "finish") {
    return (
      <>
        <CommitScreen
          sessionId={session.id}
          // COMMIT-05 - the server cannot know what this phone is still
          // holding, so the pre-flight is told.
          unsyncedOps={sync.status.counts.pending + sync.status.counts.inflight}
          nameOf={nameOf}
          isAdmin={me.role === "admin"}
          onClose={() => setTab("play")}
          onCommitted={onChanged}
        />
        {nav}
      </>
    );
  }

  return (
    <>
      <main className="session">
        <header className="session-head">
          <div className="brand">
            <h1 className="display">
              Core <span>Baddy</span>
            </h1>
            {/* FR-SESS-6 — which session this is, in one line. */}
            <div className="meta">
              {formatSessionDate(session.date)} · {tournament?.name ?? session.venue ?? "Session"}{" "}
              · {entry.courtCount} {entry.courtCount === 1 ? "court" : "courts"}
            </div>
          </div>
          <div className="row">
            {/* Rule U-2 — always visible during a session. */}
            <SyncIndicator status={sync.status} onTap={() => void sync.flush()} />
          </div>
        </header>

        {sync.status.warning ? <p className="sync-warning">{sync.status.warning}</p> : null}

        {/* FR-TOUR-4 — a tournament already knows who is on next, so the Play
            tab answers the only question left: which court is this result
            for. Both are on screen, so Won is still tap one. */}
        {tab === "play" && tournament ? (
          <TournamentPlay
            tournament={tournament}
            error={error}
            onChanged={onChanged}
            onWon={(fixture, side) => {
              setPending({
                winnerSide: side,
                sideA: fixture.sideA.map((p) => p.playerId),
                sideB: fixture.sideB.map((p) => p.playerId),
                fixture,
              });
            }}
          />
        ) : null}

        {tab === "play" && !tournament ? (
          <PlayScreen
            view={view}
            nameOf={nameOf}
            sides={sides}
            waiting={waiting}
            gameType={gameType}
            target={target}
            onTarget={setTarget}
            court={court}
            awaitingCourt={awaitingCourt}
            override={override}
            swapSlot={swapSlot}
            error={error}
            onSwapSlot={setSwapSlot}
            onOverride={setOverride}
            onGameType={setGameType}
            onCourt={setCourt}
            onWon={(side) => {
              if (sides) setPending({ winnerSide: side, ...sides });
            }}
            onShowRoster={() => setShowRoster(true)}
            onChanged={onChanged}
          />
        ) : null}

        {tab === "games" ? (
          <GamesList
            games={games}
            queued={queuedGames}
            nameOf={nameOf}
            targets={[entry.targetScore, ...entry.alternateTargets]}
            optionsByTarget={entry.scoreOptionsByTarget}
            onChanged={onChanged}
          />
        ) : null}

        {tab === "table" ? (
          <>
            <div className="row space-between stats-switch">
              <div className="eyebrow">{allTime ? "Committed sessions only" : "This session"}</div>
              <div className="row">
                {/* BR-COMMIT-2 — today and all time are the same question over
                    different games, so they are one screen with a switch. */}
                <button
                  className={allTime ? "chip" : "chip chip-on"}
                  onClick={() => setAllTime(false)}
                >
                  Today
                </button>
                <button
                  className={allTime ? "chip chip-on" : "chip"}
                  onClick={() => setAllTime(true)}
                >
                  All time
                </button>
              </div>
            </div>

            {allTime ? (
              <ClubBoard />
            ) : tournament ? (
              /* FR-TOUR-5 — the tournament's own table, ranked the way the
                 tournament was announced: a flat point a win, ties on score
                 difference. The session's Match Point table is a different
                 question and stays under All time. */
              <TournamentTable
                tournament={tournament}
                isAdmin={me.role === "admin"}
                onChanged={onChanged}
              />
            ) : (
              <Standings
                report={standingsShown ?? view.standings}
                pairs={view.pairs}
                onSwitchType={(type) => {
                  if (type === (standingsShown ?? view.standings).gameType) return;
                  void api.standings(session.id, type).then(setStandingsShown);
                }}
              />
            )}
          </>
        ) : null}

        {tab === "settings" ? (
          <>
            <YouScreen
              me={me}
              onSignedOut={onSignedOut}
              onOpenSession={onOpenSession}
              onShowGuide={onShowGuide}
            />
            <h2>This session</h2>
            <button className="quiet" onClick={onLeave}>
              Open a different session
            </button>
          </>
        ) : null}

        {toast ? (
          <div className="toast" role="status">
            <span>{toast.text}</span>
            <button
              className="link"
              onClick={() => {
                const gameId = toast.gameId;
                setToast(null);
                void api.undoGame(gameId).then(onChanged);
              }}
            >
              Undo
            </button>
          </div>
        ) : null}
      </main>
      {nav}
    </>
  );
}

/**
 * The bottom bar — the mockup's, and for the mockup's reason: on a phone a tab
 * bar is the only navigation that is always within thumb reach and never
 * scrolled away.
 *
 * Two counts sit on it. Games is how many results the session has, which is
 * the number people ask each other. The one on End counts what this phone has
 * not sent — the only number on screen that means "do not close the app yet",
 * so it goes where it cannot be scrolled past.
 */
function TabBar({
  tab,
  onTab,
  games,
  unsent,
}: {
  tab: Tab;
  onTab: (tab: Tab) => void;
  games: number;
  unsent: number;
}): React.JSX.Element {
  const tabs: Array<{ id: Tab; short: string; long: string; badge?: number }> = [
    { id: "play", short: "Play", long: "Court" },
    { id: "games", short: "Games", long: "List", badge: games || undefined },
    { id: "table", short: "Table", long: "Stats" },
    { id: "finish", short: "End", long: "Finish", badge: unsent || undefined },
    { id: "settings", short: "You", long: "Account" },
  ];

  return (
    <nav className="tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          className={t.id === tab ? "on" : undefined}
          aria-current={t.id === tab ? "page" : undefined}
          onClick={() => onTab(t.id)}
        >
          <b>{t.short}</b>
          {t.long}
          {t.badge ? (
            <span className={t.id === "finish" ? "badge badge-warn" : "badge"}>{t.badge}</span>
          ) : null}
        </button>
      ))}
    </nav>
  );
}
